import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@centraoneplus/ui';
import { ticketService, type UndoSendOperation } from '@services/index';
import { localDeadline, undoErrorCode, undoErrorStatus, type ReplySnapshot } from './undoSend';
import {
  forgetPendingSend,
  loadPendingSends,
  pendingSendsStorageKey,
  savePendingSend,
  type StoredPendingSend,
} from './undoSendStore';

export type PendingSendPhase = 'counting' | 'undoing' | 'sending';

export interface PendingSend {
  operationId: string;
  phase: PendingSendPhase;
  /** Local-clock time the email goes out. */
  deadline: number;
  undoSeconds: number;
  /** "ananya@acme.com +1" — who the reply is addressed to. */
  recipientsLabel: string;
  snapshot: ReplySnapshot;
  /** When the agent pressed Send (local clock) — shown on the pending reply. */
  startedAt: number;
}

interface UseUndoSendOptions {
  /** The email was sent: refresh the thread so the recorded reply appears. */
  onSent: (operationId: string) => void | Promise<void>;
  /** Put an undone (or failed) reply back into the composer. */
  onRestore: (snapshot: ReplySnapshot) => void;
  /** Pending sends belong to one ticket; changing it drops them (the emails still go out). */
  scopeKey?: string;
  /**
   * Remember pending sends in this browser under this key (user + ticket) so a
   * reload, or closing and reopening the ticket, brings the countdown and Undo
   * back while the window is still open. Omit to keep them in memory only.
   */
  persistKey?: string;
}

const POLL_INTERVAL_MS = 1_000;
// Long enough to cover the gateway's reconcile delay after an interrupted send.
const MAX_POLLS = 120;
// Once sent, wait for the reply to be recorded on the ticket (normally ~1s).
const MAX_POLLS_AFTER_SENT = 30;
// If it still was not recorded, refresh the thread once more after this.
const LATE_REFRESH_MS = 30_000;

function labelFor(snapshot: ReplySnapshot, fallback?: string): string {
  const to = snapshot.recipients?.to ?? [];
  const extra = (snapshot.recipients?.cc?.length ?? 0) + Math.max(0, to.length - 1);
  const first = to[0] || fallback || 'the customer';
  return extra > 0 ? `${first} +${extra}` : first;
}

/**
 * Tracks email replies held for their undo window: the countdown, Undo, and
 * confirming the outcome once the window has passed. The server is the source
 * of truth — the local countdown only drives the UI, and every decision (undo
 * accepted, too late, sent, failed) comes from the backend.
 */
export function useUndoSend({ onSent, onRestore, scopeKey, persistKey }: UseUndoSendOptions) {
  const toast = useToast();
  const [pending, setPending] = useState<PendingSend[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [announcement, setAnnouncement] = useState('');

  const pendingRef = useRef<PendingSend[]>([]);
  pendingRef.current = pending;
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const watching = useRef(new Set<string>());
  // Picked up on reopen: if one turns out cancelled, it was undone elsewhere
  // (another tab), which already restored it — don't fill this composer too.
  const adopted = useRef(new Set<string>());
  const lateRefreshes = useRef(new Set<ReturnType<typeof setTimeout>>());
  const alive = useRef(true);
  const generation = useRef(0);
  // Latest callbacks without re-creating every handler.
  const callbacks = useRef({ onSent, onRestore, toast });
  callbacks.current = { onSent, onRestore, toast };
  const persistRef = useRef(persistKey);
  persistRef.current = persistKey;

  const clearTimer = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const remove = useCallback(
    (id: string) => {
      clearTimer(id);
      watching.current.delete(id);
      adopted.current.delete(id);
      if (persistRef.current) forgetPendingSend(persistRef.current, id);
      setPending((prev) => prev.filter((p) => p.operationId !== id));
    },
    [clearTimer],
  );

  const setPhase = useCallback((id: string, phase: PendingSendPhase) => {
    setPending((prev) => prev.map((p) => (p.operationId === id ? { ...p, phase } : p)));
  }, []);

  /** After the deadline: ask the server until the email is sent, failed or cancelled. */
  const watch = useCallback(
    async (id: string) => {
      if (watching.current.has(id)) return;
      watching.current.add(id);
      const gen = generation.current;
      clearTimer(id);
      setPhase(id, 'sending');
      setAnnouncement('Sending email…');

      const stillMine = () => alive.current && gen === generation.current && watching.current.has(id);
      let sentPolls = 0;

      for (let poll = 0; poll < MAX_POLLS; poll++) {
        if (!stillMine()) return;
        let op: UndoSendOperation | null = null;
        try {
          op = await ticketService.getUndoSendStatus(id);
        } catch (error) {
          if (undoErrorStatus(error) === 404) {
            if (!stillMine()) return;
            remove(id);
            callbacks.current.toast.warning("Couldn't confirm the email", 'Check the conversation to see whether it was sent.');
            return;
          }
          // Transient: keep asking.
        }
        if (!stillMine()) return;

        if (op?.status === 'sent') {
          sentPolls++;
          if (op.ticketMessageId) {
            remove(id);
            setAnnouncement('Email sent successfully.');
            callbacks.current.toast.success('Email sent successfully');
            await callbacks.current.onSent(id);
            return;
          }
          if (sentPolls >= MAX_POLLS_AFTER_SENT) {
            // The email is out; only the ticket record is late. Say so, and look again later.
            remove(id);
            setAnnouncement('Email sent successfully.');
            callbacks.current.toast.success('Email sent successfully', 'It will appear in the conversation shortly.');
            await callbacks.current.onSent(id);
            const late = setTimeout(() => {
              if (alive.current && gen === generation.current) void callbacks.current.onSent(id);
            }, LATE_REFRESH_MS);
            lateRefreshes.current.add(late);
            return;
          }
        } else if (op?.status === 'cancelled') {
          const item = pendingRef.current.find((p) => p.operationId === id);
          const elsewhere = adopted.current.has(id);
          remove(id);
          setAnnouncement('Email sending cancelled.');
          callbacks.current.toast.info('Email sending cancelled');
          if (item && !elsewhere) callbacks.current.onRestore(item.snapshot);
          return;
        } else if (op?.status === 'failed') {
          const item = pendingRef.current.find((p) => p.operationId === id);
          remove(id);
          setAnnouncement('The email could not be sent.');
          callbacks.current.toast.error(
            'Email not sent',
            op.error || 'The email could not be sent. Your reply is back in the composer.',
          );
          if (item && op.errorCode !== 'outcome_unknown') callbacks.current.onRestore(item.snapshot);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }

      if (!stillMine()) return;
      remove(id);
      callbacks.current.toast.warning(
        'Still sending',
        'Your reply is taking longer than usual. It will appear in the conversation once it has been sent.',
      );
      await callbacks.current.onSent(id);
    },
    [clearTimer, remove, setPhase],
  );

  const armDeadline = useCallback(
    (id: string, deadline: number) => {
      clearTimer(id);
      const timer = setTimeout(() => void watch(id), Math.max(0, deadline - Date.now()) + 300);
      timers.current.set(id, timer);
    },
    [clearTimer, watch],
  );

  /** Begin tracking a held reply returned by the server. */
  const start = useCallback(
    (operation: UndoSendOperation, snapshot: ReplySnapshot, receivedAt = Date.now(), fallbackRecipient?: string) => {
      const deadline = localDeadline(operation.deadline, operation.undoSeconds, receivedAt);
      const item: PendingSend = {
        operationId: operation.operationId,
        phase: 'counting',
        deadline,
        undoSeconds: operation.undoSeconds,
        recipientsLabel: labelFor(snapshot, fallbackRecipient),
        snapshot,
        startedAt: receivedAt,
      };
      setPending((prev) => [...prev.filter((p) => p.operationId !== item.operationId), item]);
      setNow(Date.now());
      if (persistRef.current) {
        const { phase: _phase, ...stored } = item;
        savePendingSend(persistRef.current, stored);
      }

      if (operation.status === 'scheduled' || operation.status === 'preparing') {
        setAnnouncement(
          `Email will be sent in ${Math.max(1, Math.round((deadline - Date.now()) / 1000))} seconds. Use Undo to cancel.`,
        );
        armDeadline(item.operationId, deadline);
      } else {
        // A retried request can come back already past its window.
        void watch(item.operationId);
      }
    },
    [armDeadline, watch],
  );

  /** Undo: only the server can say whether it was in time. */
  const undo = useCallback(
    async (id: string) => {
      const item = pendingRef.current.find((p) => p.operationId === id);
      if (!item || item.phase !== 'counting') return;
      setPhase(id, 'undoing');
      clearTimer(id);

      try {
        await ticketService.cancelUndoSend(id);
        remove(id);
        setAnnouncement('Email sending cancelled.');
        callbacks.current.toast.info('Email sending cancelled');
        callbacks.current.onRestore(item.snapshot);
      } catch (error) {
        const status = undoErrorStatus(error);
        const code = undoErrorCode(error);
        if (status === 409 && (code === 'UNDO_ALREADY_SENT' || code === 'UNDO_WINDOW_EXPIRED')) {
          callbacks.current.toast.warning('Too late to undo', 'The email has already been sent.');
          void watch(id);
        } else if (status === 409 && code === 'UNDO_SEND_FAILED') {
          remove(id);
          callbacks.current.toast.error('Email not sent', 'The email failed to send. Your reply is back in the composer.');
          callbacks.current.onRestore(item.snapshot);
        } else if (status === 404) {
          remove(id);
          callbacks.current.toast.error("Couldn't undo", 'This email could not be found. Check the conversation.');
          await callbacks.current.onSent(id);
        } else {
          // Network / server trouble: we do not know — the email may still go out.
          callbacks.current.toast.error("Couldn't undo", 'We could not reach the server. The email may still be sent.');
          if (Date.now() < item.deadline) {
            setPhase(id, 'counting');
            armDeadline(id, item.deadline);
          } else {
            void watch(id);
          }
        }
      }
    },
    [armDeadline, clearTimer, remove, setPhase, watch],
  );

  // Reopened (reload, new tab, back to this ticket): pick up replies still held
  // for their window. The server says where each one stands; only one it is
  // still holding gets its countdown and Undo back.
  useEffect(() => {
    if (!persistKey) return;
    const key = persistKey;
    const gen = generation.current;
    const checking = new Set<string>();
    let disposed = false;
    const current = () =>
      !disposed && alive.current && gen === generation.current && persistRef.current === key;

    const adopt = (entry: StoredPendingSend, counting: boolean) => {
      adopted.current.add(entry.operationId);
      setPending((prev) =>
        prev.some((p) => p.operationId === entry.operationId)
          ? prev
          : [...prev, { ...entry, phase: counting ? 'counting' : 'sending' }],
      );
      setNow(Date.now());
      if (counting) {
        setAnnouncement(
          `Email will be sent in ${Math.max(1, Math.round((entry.deadline - Date.now()) / 1000))} seconds. Use Undo to cancel.`,
        );
        armDeadline(entry.operationId, entry.deadline);
      } else {
        void watch(entry.operationId);
      }
    };

    const restore = async (entry: StoredPendingSend) => {
      const id = entry.operationId;
      if (checking.has(id) || pendingRef.current.some((p) => p.operationId === id)) return;
      checking.add(id);
      let op: UndoSendOperation | null = null;
      try {
        op = await ticketService.getUndoSendStatus(id);
      } catch (error) {
        if (!current()) return;
        if (undoErrorStatus(error) === 404) {
          forgetPendingSend(key, id);
          return;
        }
        // Can't reach the server: show it as it was. Undo and the deadline both
        // ask the server, so nothing is decided locally.
        adopt(entry, Date.now() < entry.deadline);
        return;
      }
      if (!current() || pendingRef.current.some((p) => p.operationId === id)) return;

      switch (op?.status) {
        case 'preparing':
        case 'scheduled':
          adopt(entry, Date.now() < entry.deadline);
          return;
        case 'sending':
          adopt(entry, false);
          return;
        case 'sent':
          // Already in the thread once recorded; otherwise wait for it like a fresh send.
          if (op.ticketMessageId) forgetPendingSend(key, id);
          else adopt(entry, false);
          return;
        case 'failed':
          forgetPendingSend(key, id);
          callbacks.current.toast.error(
            'Email not sent',
            op.error || 'The email could not be sent. Your reply is back in the composer.',
          );
          if (op.errorCode !== 'outcome_unknown') callbacks.current.onRestore(entry.snapshot);
          return;
        default:
          // Cancelled (undone here or in another tab) or unknown.
          forgetPendingSend(key, id);
      }
    };

    const restoreAll = () => {
      for (const entry of loadPendingSends(key)) void restore(entry);
    };
    restoreAll();

    // A reply sent from another tab on the same ticket shows up here too.
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === pendingSendsStorageKey(key)) restoreAll();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      disposed = true;
      window.removeEventListener('storage', onStorage);
    };
  }, [persistKey, armDeadline, watch]);

  // Tick the countdown only while something is counting down.
  const counting = pending.some((p) => p.phase === 'counting');
  useEffect(() => {
    if (!counting) return;
    const ticker = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(ticker);
  }, [counting]);

  // Leaving the page during the window keeps the email but loses Undo — say so.
  // Not needed when pending sends are remembered: reopening brings Undo back.
  useEffect(() => {
    if (!counting || persistKey) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [counting, persistKey]);

  // A different ticket: forget this one's pending sends (they still go out) so
  // nothing is ever restored into the wrong ticket's composer.
  useEffect(() => {
    const timerMap = timers.current;
    const watchSet = watching.current;
    const adoptedSet = adopted.current;
    return () => {
      generation.current++;
      for (const timer of timerMap.values()) clearTimeout(timer);
      timerMap.clear();
      watchSet.clear();
      adoptedSet.clear();
      setPending([]);
    };
  }, [scopeKey]);

  useEffect(() => {
    alive.current = true;
    const late = lateRefreshes.current;
    return () => {
      alive.current = false;
      for (const timer of late) clearTimeout(timer);
      late.clear();
    };
  }, []);

  return { pending, now, announcement, start, undo };
}
